import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { CONNECT_DOMAIN, REGISTRAR_API } from '../shared/app-token';
import { ScopeInstance } from '../types/scope-instance';
import { ScopeServer } from '../types/scope-server';

import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SharedService } from './shared.service';

/**
 * registrar service handles api request which are related with scope clients
 */
@Injectable({
    providedIn: 'root'
})
export class RegistrarService {
    #apiUrl = inject(REGISTRAR_API);
    #connectDomain = inject(CONNECT_DOMAIN);

    #httpClient = inject(HttpClient);
    #localStorageService = inject(LocalStorageService);
    #sharedService = inject(SharedService);

    /**
     * get server for an organization
     * @param       id of organisation
     * @returns     scope servers
     */
    getScopeServer(id: string): Observable<ScopeServer[]> {
        const dto = {
            organizationOID: id,
            connectSystemType: this.#connectDomain
        };
        const params = new URLSearchParams(dto);

        return this.#httpClient
            .get<ScopeServer[]>(`${this.#apiUrl}/scopeInstallations?${params}`)
            .pipe((servers) => servers);
    }

    /**
     * get list of scope instances
     * @returns    json object with list of scope instances
     */
    getScopeInstances(): Observable<ScopeInstance[]> {
        return this.#httpClient.get<ScopeInstance[]>(`${this.#apiUrl}/domains`).pipe((instances) => instances);
    }

    /**
     * get scope instance by id
     * @param id            id of scope instance
     * @returns {object}    json object with instance details
     */
    getScopeInstanceById(id: string): Observable<ScopeInstance> {
        return this.#httpClient.get<ScopeInstance>(`${this.#apiUrl}/domains/${id}`).pipe(
            map((instance) => instance),
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse) {
                    try {
                        this.#sharedService.handleServerSideError(err);
                    } catch (e) {
                        console.log('A server error occurred', e);
                    }
                }
                return of(err);
            })
        );
    }

    /**
     * get server details from registrar
     * and sort names of organizations depending on user language
     * @param instance  name of scope instance
     * @returns {Array} list of scope instance details
     */
    getScopeInstanceDetails(instance: string) {
        const account = this.#localStorageService.load('account');
        if (account) {
            const language = account.preferredLanguage ?? navigator.language;
            const locale = language.slice(0, 2);

            return this.#httpClient.get<ScopeInstance>(`${this.#apiUrl}/domains/${instance}`).pipe(
                map((scopeDomain: ScopeInstance) => {
                    const serverDetails = scopeDomain;

                    const collator = new Intl.Collator(locale);
                    serverDetails.organizations.sort((a: { name: string }, b: { name: string }) =>
                        collator.compare(a.name, b.name)
                    );

                    /** sort by preferred language */
                    return serverDetails;
                }),
                catchError((err: any) => {
                    if (err instanceof HttpErrorResponse) {
                        try {
                            this.#sharedService.handleServerSideError(err);
                        } catch (e) {
                            console.log('A server error occurred', e);
                        }
                    }
                    return of(err);
                })
            );
        } else {
            this.#sharedService.handleSessionExpired();
            return of(null);
        }
    }
}
